(ns admin.components.composite.moment-editor.edit
  (:require
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [admin.components.composite.moment-editor.edit.pdf.behavior :as edit-pdf-behavior]
   ;; [admin.components.composite.moment-editor.edit.pdf.halos :as edit-pdf-halos]
   [admin.components.composite.moment-editor.edit.pdf.placement :as edit-pdf-placement]

   [admin.components.composite.moment-editor.edit.video.halos :as edit-video-halos]
   [admin.components.composite.moment-editor.edit.video.placement-and-behavior
    :as edit-video-placement-and-behavior]

   [admin.components.composite.moment-editor.edit.details :as edit-details]
   [admin.components.composite.moment-editor.edit.learning-objective :as edit-learning-objective]
   [admin.components.composite.moment-editor.edit.resources :as edit-resources]
   [admin.components.composite.moment-editor.edit.visibility :as edit-visibility]

   [admin.events.moments :as >moments]
   [admin.subscriptions.moments :as <moments]
   [admin.events.questions :as >questions]

   [admin.utils :as au]

   [kyber.components.base.button :as button]
   [kyber.components.base.icons :as icons]
   [kyber.components.composite.loading :as loading]
   [kyber.components.base.text :as text]

   [kyber.styles.core :refer [gs styles->classes]]))

(def classes
  (styles->classes
   {:container
    {:padding (gs [:spacing :p28])
     :width "100%"}

    :items-center
    {:display :flex
     :align-items :center}

    :panel-list-overlay
    {:position :absolute
     :top 0
     :left 0
     :z-index 1
     :width "100%"
     :height "100%"
     :background (gs [:colors :true-white])
     :border-radius (gs [:radius :r7])
     :opacity 0.80
     :display :flex
     :justify-content :center
     :align-items :center}

    :panel-container
    {:margin-top (gs [:spacing :p20])
     :width "100%"
     :min-height "200px"
     :position :relative
     :background (gs [:colors :true-white])
     :box-shadow (gs [:shadows :shadow-6])
     :border-radius (gs [:radius :r7])}

    :panel
    {:border-bottom (str "1px solid " (gs [:colors :primary-gray-100]))
     "&:first-child"
     {:border-radius [(gs [:radius :r7])
                      (gs [:radius :r7])
                      (gs [:radius :r0])
                      (gs [:radius :r0])]}

     "&:last-child"
     {:border :none
      :border-radius [(gs [:radius :r0])
                      (gs [:radius :r0])
                      (gs [:radius :r7])
                      (gs [:radius :r7])]}}

    :panel-header
    {:padding [(gs [:spacing :p12])
               (gs [:spacing :p16])]
     :cursor :pointer
     :border-radius :inherit
     "&:hover"
     {:background (gs [:colors :gray-scale-50])}}

    :header-icon
    {:color (gs [:colors :primary-lite-500])
     :width (gs [:spacing :p20])}

    :panel-selected
    {:min-height (gs [:spacing :p52])}}))

(def ^:private pdf-panels
  [{:panel-key :placement
    :view      edit-pdf-placement/render
    :title     "Placement & Display"}
   {:panel-key :details
    :view      edit-details/render
    :title     "Type"}
   {:panel-key :behavior
    :view      edit-pdf-behavior/render
    :title     "Behavior"}
   {:panel-key :learning-objective
    :view      edit-learning-objective/render
    :title     "Learning Objective"}
   {:panel-key :resources
    :view      edit-resources/render
    :title     "Resources"}
   {:panel-key :visibility
    :view      edit-visibility/render
    :title     "Visibility"}
   ;; TODO: This will go back in once PDF moments have
   ;;       been revisited
   #_{:panel-key :halos
      :view      edit-pdf-halos/render
      :title     "Halos"}])

(def ^:private video-panels
  [{:panel-key :placement
    :view      edit-video-placement-and-behavior/render
    :title     "Placement & Behavior"}
   {:panel-key :details
    :view      edit-details/render
    :title     "Type"}
   {:panel-key :learning-objective
    :view      edit-learning-objective/render
    :title     "Learning Objective"}
   {:panel-key :resources
    :view      edit-resources/render
    :title     "Resources"}
   {:panel-key :halos
    :view      edit-video-halos/render
    :title     "Halos"}
   {:panel-key :visibility
    :view      edit-visibility/render
    :title     "Visibility"}])

(defn- panel-title
  [{:keys [panel-key title]} moment editor-state]
  (if (= :details panel-key)
    (if-let [title-type (au/moment->type-display moment editor-state)]
      (str title ": " title-type)
      title)
    title))

(defn- section-panel [{:keys [selected? editor-state panel moment dispatch-local]}]
  (let [{:keys [panel-key view]}
        panel]
    [:div {:class [(:panel classes)
                   (when selected?
                     (:panel-selected classes))]}
     [:div {:class [(:items-center classes)
                    (:panel-header classes)]
            :on-click #(dispatch-local (if selected?
                                         [:unset-active-panel]
                                         [:set-active-panel panel-key]))}

      [icons/icon-sm {:class (:header-icon classes)}
       (if selected? :chevron-down :chevron-right)]
      [text/title-20 :span {} (panel-title panel @moment editor-state)]]
     (when selected?
       [view {:moment moment
              :editor-state editor-state
              :dispatch-local dispatch-local}])]))

(defn- header [{:keys [dispatch-local is-updating]}]
  [:div {:class (:items-center classes)
         :style {:justify-content :space-between}}
   [text/title-30 :span {} "Edit Moment"]
   [button/secondary-button
    {:on-click #(dispatch-local [:exit-moment-edit-view {:deleted? false}])
     :disabled is-updating}
    "Close"]])

(defn render
  [_]
  (let [moment (re-frame/subscribe [::<moments/active-moment])]
    
    (reagent/create-class
     {:display-name
      "Edit moment"

      :reagent-render
      (fn [{:keys [editor-state dispatch-local show-visibility-panel?]}]
        (let [{:keys [pdf-activity? video-activity?]} @editor-state
              activity-panels (cond
                                pdf-activity? pdf-panels
                                video-activity? video-panels)
              panels (if show-visibility-panel?
                       activity-panels
                       (filterv #(not= (:panel-key %) :visibility) activity-panels))
              is-updating (re-frame/subscribe [:request/any-dispatching
                                               ::>moments/create-moment
                                               ::>moments/update-moment
                                               ::>moments/remove-moment
                                               ::>moments/remove-resource
                                               ::>questions/update-question
                                               ::>questions/create-question])]
          [:div {:class (:container classes)}
           [header {:dispatch-local dispatch-local
                    :is-updating @is-updating}]
           [:div (cond-> {:class (:panel-container classes)}
                   (nil? @moment)
                   (assoc :style {:justify-content :center
                                  :align-items :center}))
            (if (some? @moment)
              (let [selected-panel (:active-panel @editor-state)]

                [:<>
                 [:div
                  (when (true? @is-updating)
                    [:div {:class (:panel-list-overlay classes)}
                     [loading/circles]])

                  (for [{:keys [panel-key] :as panel} panels]
                    ^{:key panel}
                    [section-panel {:dispatch-local dispatch-local
                                    :editor-state editor-state
                                    :selected? (= panel-key selected-panel)
                                    :panel panel
                                    :moment moment}])]])
              [:div {:style {:position :absolute
                             :left "50%"
                             :top "50%"
                             :margin-top "-40px"}}
               [loading/circles]])]]))})))
